import { TransportPrincipalType, AddressType, ConnectionType, AppointmentStatus } from '@enums/transport.enum';
import { FuelType, BodyType } from '@enums/vehicle.enum';
import { Helpers } from '@utils/helpers';
import { TransportAddress } from './transport-address.dto';
import { AdditionalServiceDto } from '@dtos/additional-services.dto';
import { IncidentRequestDto } from '@dtos/quality/incident.request.dto';
import { DriverSimpleDataDto } from '@dtos/driver.dto';
import { TransportDetailGetter } from './transport.get.dto';

// ============================================
// HISTORY DTOs (Existentes)
// ============================================

export class TransportHistoryListParamsDto{

    page?: number;
    page_size?: number;
    search?: string;
    reference_number?: string;
    transport_number?: string;
    reservation_number?: string;
    created_from?: string;
    departure_province?: string;
    arrival_province?: string;
    driver_email?: string;
    date_range_start?: string;
    date_range_end?: string;
    transport_principal_type?: string;
    tags?: number[];
    client_name?: string;
    transport_status?: string;
    driver_name?: string;
    duration_hours?: string;
    admin_name?: string;
    sort_column?: string;
    sort_direction?: 'asc' | 'desc';
    ordering?: string;
    license_plate?: string;
    has_appointment?: boolean;
    appointment_status?: string;
}

export class TransportHistoryListItemDto {
    id: number;
    has_appointment: boolean;
    appointment_status: AppointmentStatus | null;
    get appointment_management(): boolean { return this.has_appointment; }
    set appointment_management(value: boolean) { this.has_appointment = value; }
    reference_number: string;
    reservation_number: string;
    transport_type: string | null;
    transport_status: string;
    transport_principal_type: string;
    price: string;
    origin_address: string;
    destination_address: string;
    kilometers: string;
    duration_hours: number;
    tags: number[];
    // Nuevos campos de objetos anidados
    client: { id: number; name: string; logo: string } | null;
    driver: { id: number; name: string; last_name: string; dni: string | null; nie: string | null; cif: string | null; validated: boolean; phone: string; email: string } | null;
    admin_name: string;
    start_date: string | null;
    end_date: string | null;
    expected_start_date: string | null;
    expected_end_date: string | null;
    is_blocked: boolean;
    change_requests: any[];
    // Campos legacy
    client_name: string;
    driver_name: string | null;

    constructor(data: any = {}) {
        this.id = data.id || 0;
        this.reference_number = data.reference_number || '';
        this.reservation_number = data.reservation_number || '';
        this.transport_type = data.transport_type ?? null;
        this.transport_status = data.transport_status || '';
        this.transport_principal_type = data.transport_principal_type || '';
        this.has_appointment = data.has_appointment ?? data.appointment_management ?? false;
        this.appointment_status = data.appointment_status ?? null;
        this.price = data.price || '0.00';
        this.origin_address = data.origin_address || '';
        this.destination_address = data.destination_address || '';
        this.kilometers = data.kilometers || '0.00';
        this.duration_hours = data.duration_hours || 0;
        this.tags = data.tags || [];
        // Nuevos campos
        this.client = data.client ?? null;
        this.driver = data.driver ?? null;
        this.admin_name = data.admin_name || '';
        this.start_date = data.start_date ?? null;
        this.end_date = data.end_date ?? null;
        this.expected_start_date = data.expected_start_date ?? null;
        this.expected_end_date = data.expected_end_date ?? null;
        this.is_blocked = data.is_blocked || false;
        this.change_requests = data.change_requests || [];
        // Campos legacy
        this.client_name = data.client_name ?? (data.client?.name ?? '');
        this.driver_name = data.driver_name ?? (data.driver ? `${data.driver.name} ${data.driver.last_name}` : null);
    }

    static fromJson(json: any): TransportHistoryListItemDto {
        return new TransportHistoryListItemDto(json);
    }

    toJson(): any {
        return {
            id: this.id,
            reference_number: this.reference_number,
            reservation_number: this.reservation_number,
            client: this.client,
            client_name: this.client_name,
            has_appointment: this.has_appointment,
            appointment_status: this.appointment_status,
            appointment_management: this.has_appointment,
            transport_type: this.transport_type,
            transport_status: this.transport_status,
            transport_principal_type: this.transport_principal_type,
            price: this.price,
            origin_address: this.origin_address,
            destination_address: this.destination_address,
            kilometers: this.kilometers,
            duration_hours: this.duration_hours,
            tags: this.tags,
            driver: this.driver,
            driver_name: this.driver_name,
            admin_name: this.admin_name,
            start_date: this.start_date,
            end_date: this.end_date,
            expected_start_date: this.expected_start_date,
            expected_end_date: this.expected_end_date,
            is_blocked: this.is_blocked,
            change_requests: this.change_requests
        };
    }

    copyWith(updates: Partial<TransportHistoryListItemDto>): TransportHistoryListItemDto {
        return new TransportHistoryListItemDto({
            ...this.toJson(),
            ...updates
        });
    }
}

// ============================================
// ASSIGNED LIST DTOs
// ============================================

export class TransportAssignedListParamsDto{

    page?: number;
    page_size?: number;
    search?: string;
    reference_number?: string;
    transport_number?: string;
    reservation_number?: string;
    created_from?: string;
    created_to?: string;
    departure_province?: string;
    arrival_province?: string;
    driver_email?: string;
    init_date?: string;
    end_date?: string;
    transport_principal_type?: string;
    tags?: number[];
    client_name?: string;
    transport_status?: string;
    driver_name?: string;
    admin_name?: string;
    sort_column?: string;
    sort_direction?: 'asc' | 'desc';
    ordering?: string;
    license_plate?: string;
    has_appointment?: boolean;
    appointment_status?: string;
}

export class TransportAssignedListItemDto {
    id: number;
    is_express: boolean;
    
    transport_principal_type: string;
    transport_type: string | null;
    has_ferry: boolean;
    has_appointment: boolean;
    appointment_status: AppointmentStatus | null;
    get appointment_management(): boolean { return this.has_appointment; }
    set appointment_management(value: boolean) { this.has_appointment = value; }
    comment: string | null;
    group_number: number | null;
    reservation_number: string;
    reference_number: string;
    is_blocked: boolean;
    is_mission_blocked: boolean;
    invoiced: boolean;
    transport_status: string;
    cancelled_comment: string | null;
    cancelled_reason: string | null;
    vehicle?: TransportVehicleDto | null;
    // Nuevos campos de objetos anidados
    client: { id: number; name: string; logo: string } | null;
    driver: { id: number; name: string; last_name: string; dni: string | null; nie: string | null; cif: string | null; validated: boolean; phone: string; email: string } | null;
    admin: { id: number; name: string } | null;
    best_budget: { id: number; total_price: string; departure_datetime: string; arrival_datetime: string; base_price: string; fuel_price: string; transport_cost_outbound: string; transport_cost_return: string; optional_expenses_price: string; accommodation_cost: string; toll_expense: string; per_diem_cost: string; budget_status: string; invalidation_reason: string | null; margin_eur: number; margin_pct: number } | null;
    num_budgets: number;
    origin_address: string;
    origin_city: string;
    destination_address: string;
    destination_city: string;
    // Campos legacy para compatibilidad
    client_id: number | null;
    client_name: string | null;
    driver_id: number | null;
    driver_name: string | null;
    admin_id: number | null;
    admin_name: string | null;
    tags: number[];
    kilometers: string;
    price: string;
    stage_discount: string;
    duration: number;
    created_date: string | null;
    modified_date: string | null;
    show_timeline: boolean;
    timeline: any | null;
    countries: string[];
    change_requests: any[];
    legs: any[];
    start_date: string | null;
    end_date: string | null;
    expected_start_date: string | null;
    expected_end_date: string | null;

    constructor(data: any = {}) {
        this.id = data.id || 0;
        this.is_express = data.is_express || false;
        this.transport_principal_type = data.transport_principal_type || '';
        this.transport_type = data.transport_type ?? null;
        this.has_ferry = data.has_ferry || false;
        this.has_appointment = data.has_appointment ?? data.appointment_management ?? false;
        this.appointment_status = data.appointment_status ?? null;
        this.comment = data.comment ?? null;
        this.group_number = data.group_number ?? null;
        this.reservation_number = data.reservation_number || '';
        this.reference_number = data.reference_number || '';
        this.is_blocked = data.is_blocked || false;
        this.is_mission_blocked = data.is_mission_blocked || false;
        this.invoiced = data.invoiced || false;
        this.transport_status = data.transport_status || '';
        this.cancelled_comment = data.cancelled_comment ?? null;
        this.cancelled_reason = data.cancelled_reason ?? null;
        // Nuevos campos de objetos anidados
        this.client = data.client ?? null;
        this.driver = data.driver ?? null;
        this.admin = data.admin ?? null;
        this.best_budget = data.best_budget ?? null;
        this.num_budgets = data.num_budgets || 0;
        this.origin_address = data.origin_address || '';
        this.origin_city = data.origin_city || '';
        this.destination_address = data.destination_address || '';
        this.destination_city = data.destination_city || '';
        this.vehicle = data.vehicle ?? null;
        // Campos legacy
        this.client_id = data.client_id ?? (data.client?.id ?? null);
        this.client_name = data.client_name ?? (data.client?.name ?? null);
        this.driver_id = data.driver_id ?? (data.driver?.id ?? null);
        this.driver_name = data.driver_name ?? (data.driver ? `${data.driver.name} ${data.driver.last_name}` : null);
        this.admin_id = data.admin_id ?? (data.admin?.id ?? null);
        this.admin_name = data.admin_name ?? (data.admin?.name ?? null);
        this.tags = data.tags || [];
        this.kilometers = data.kilometers || '0.00';
        this.price = data.price || '0.00';
        this.stage_discount = data.stage_discount || '0.00';
        this.duration = data.duration || 0;
        this.created_date = data.created_date ?? null;
        this.modified_date = data.modified_date ?? null;
        this.show_timeline = data.show_timeline || false;
        this.timeline = data.timeline ?? null;
        this.countries = data.countries || [];
        this.change_requests = data.change_requests || [];
        this.legs = data.legs || [];
        this.start_date = data.start_date ?? null;
        this.end_date = data.end_date ?? null;
        this.expected_start_date = data.expected_start_date ?? null;
        this.expected_end_date = data.expected_end_date ?? null;
    }

    static fromJson(json: any): TransportAssignedListItemDto {
        return new TransportAssignedListItemDto(json);
    }

    toJson(): any {
        return {
            id: this.id,
            is_express: this.is_express,
            transport_principal_type: this.transport_principal_type,
            transport_type: this.transport_type,
            has_ferry: this.has_ferry,
            has_appointment: this.has_appointment,
            appointment_status: this.appointment_status,
            appointment_management: this.has_appointment,
            comment: this.comment,
            group_number: this.group_number,
            reservation_number: this.reservation_number,
            reference_number: this.reference_number,
            is_blocked: this.is_blocked,
            is_mission_blocked: this.is_mission_blocked,
            invoiced: this.invoiced,
            transport_status: this.transport_status,
            cancelled_comment: this.cancelled_comment,
            cancelled_reason: this.cancelled_reason,
            client: this.client,
            driver: this.driver,
            admin: this.admin,
            best_budget: this.best_budget,
            num_budgets: this.num_budgets,
            origin_address: this.origin_address,
            origin_city: this.origin_city,
            destination_address: this.destination_address,
            destination_city: this.destination_city,
            client_id: this.client_id,
            client_name: this.client_name,
            driver_id: this.driver_id,
            driver_name: this.driver_name,
            admin_id: this.admin_id,
            admin_name: this.admin_name,
            tags: this.tags,
            kilometers: this.kilometers,
            price: this.price,
            duration: this.duration,
            created_date: this.created_date,
            modified_date: this.modified_date,
            show_timeline: this.show_timeline,
            timeline: this.timeline,
            countries: this.countries,
            change_requests: this.change_requests,
            legs: this.legs,
            start_date: this.start_date,
            end_date: this.end_date,
            expected_start_date: this.expected_start_date,
            expected_end_date: this.expected_end_date
        };
    }

    copyWith(updates: Partial<TransportAssignedListItemDto>): TransportAssignedListItemDto {
        return new TransportAssignedListItemDto({
            ...this.toJson(),
            ...updates
        });
    }
}

// ============================================
// CREATE TRANSPORT DTOs
// ============================================

export class BillingAccountToTransport {
    billing_account_id: number;

    constructor(data: any = {}) {
        this.billing_account_id = data.billing_account_id || 0;
    }

    toJson(): any {
        return {
            billing_account_id: this.billing_account_id
        };
    }
}

/**
 * DTO principal para crear un transporte
 */
export class TransportDto {
    id?: number;
    billing_account: BillingAccountToTransport;
    client_user_id: number;
    admin_id: number;
    client?: { id: number; name: string, logo: string } | null;
    is_blocked?: boolean = true;
    is_express?: boolean = false;
    is_eurotransportcar?: boolean = false;
    has_appointment?: boolean = false;
    transport_principal_type: TransportPrincipalType;
    reference_number: string;
    comment?: string = "";
    tag_ids?: number[];
    legs: TransportLegDto[] = [new TransportLegDto()];
    driver?: DriverSimpleDataDto;
    price?: string; 
    duration?: number;
    kilometers?: string;
    admin?: { id: number; name: string };
    manual_price?: number;
    has_manual_price?: boolean;
    constructor(data: any = {}) {
        this.id = data.id;
        this.billing_account = data.billing_account
            ? new BillingAccountToTransport(data.billing_account)
            : new BillingAccountToTransport();
        this.client_user_id = data.client_user_id || 0;
        this.admin_id = data.admin_id || 0;
        this.transport_principal_type = data.transport_principal_type || TransportPrincipalType.SIMPLE_MOVEMENT;
        this.reference_number = data.reference_number || '';
        this.comment = data.comment;
        this.tag_ids = data.tag_ids || [];
        this.legs = data.legs?.map((leg: any) => new TransportLegDto(leg)) || [];
        this.is_blocked = data.is_blocked !== undefined ? data.is_blocked : true;
        this.is_express = data.is_express !== undefined ? data.is_express : false;
        this.is_eurotransportcar = data.is_eurotransportcar !== undefined ? data.is_eurotransportcar : false;
        this.has_appointment = data.has_appointment !== undefined ? data.has_appointment : (data.appointment_management ?? false);
        this.manual_price = data.manual_price;
        this.has_manual_price = data.has_manual_price;
        this.price = data.price;
        this.kilometers = data.kilometers;
        this.admin = data.admin;
        if(data.driver){
            this.driver = data.driver;
        }
        
    }

    static fromJson(json: any): TransportDto {
        return new TransportDto(json);
    }

    static fromIncident(incident: IncidentRequestDto): TransportDto {
        const transport = new TransportDto();
        if (!incident || !incident.transport) return transport;

        const it = incident.transport;
        
        transport.id = it.id;
        transport.reference_number = it.reference_number;
        transport.comment = it.comment || incident.comments;
        transport.transport_principal_type = it.transport_principal_type as TransportPrincipalType;
        transport.tag_ids = it.tags || [];
        transport.price = it.price || '0.00';
        transport.is_blocked = it.is_blocked;
        transport.is_express = it.is_express;
        

        if (incident.client) {
            transport.client_user_id = incident.client.id;
            transport.client = {
                id: incident.client.id,
                name: incident.client.name,
                logo: incident.client.logo
            };
        }

        if (it.admin) {
            transport.admin_id = it.admin.id;
            transport.admin = {
                id: it.admin.id,
                name: it.admin.name
            };
        }

        if (it.legs && Array.isArray(it.legs) && it.legs.length > 0) {
            transport.legs = it.legs.map((l: any, index: number) => {
                const leg = new TransportLegDto(l);
                // Si el leg no tiene vehículos y el transporte del incidente sí, se lo asignamos al primer tramo
                if (index === 0 && (!leg.vehicles || leg.vehicles.length === 0) && it.vehicle) {
                    leg.vehicles = [new TransportVehicleDto(it.vehicle)];
                }
                return leg;
            });
        } else if (it.vehicle) {
            // Fallback si no hay legs pero hay vehículo
            const defaultLeg = new TransportLegDto();
            defaultLeg.vehicles = [new TransportVehicleDto(it.vehicle)];
            transport.legs = [defaultLeg];
        }

        return transport;
    }

    toJson(): any {

        return {
            id: this.id,
            billing_account: this.billing_account,
            client_user_id: this.client_user_id,
            admin_id: this.admin_id,
            transport_principal_type: this.transport_principal_type,
            reference_number: this.reference_number,
            comment: this.comment,
            is_blocked: this.is_blocked,
            is_express: this.is_express,
            is_eurotransportcar: this.is_eurotransportcar,
            tag_ids: this.tag_ids,
            legs: this.legs.map(leg => leg.toJson()),
            has_appointment: this.has_appointment,
            price: this.price,
            admin: this.admin,
            driver: this.driver,
            manual_price: this.manual_price,
            has_manual_price: this.has_manual_price
        };
    }

    copyWith(updates: Partial<TransportDto>): TransportDto {
        return new TransportDto({
            ...this.toJson(),
            ...updates
        });
    }
    prepareDocumet(d: TransportDocumentDto){
        if(d.id){
            delete(d.file_template);
            delete(d.document_template_id);
            delete(d.title);
        }else{
            if(!Helpers.isEmptyOrZero(d.document_template_id)){
                delete(d.title);
                delete(d.file_template);
            }else{
                delete(d.document_template_id);
            }
        }
    }
    toCreate(): Partial<TransportDto> {
        const j = this.toJson();
        if(Helpers.isEmptyOrZero(this.id)) delete(j.id);
        if(Helpers.isEmptyOrZero(this.admin_id)) delete(j.admin_id);
        for(var [ index, leg] of j.legs.entries()){
            if(index == 0){
                leg.connection_type = j.transport_principal_type == TransportPrincipalType.WITH_STOPOVER ? ConnectionType.STOPOVER : j.legs.length > 1 ? ConnectionType.STAGE : null;
            }else{
                leg.connection_type = null;
                leg.expected_departure_from = new Date(new Date(j.legs[index - 1].expected_arrival_to).setMinutes(1)).toISOString();
                leg.expected_departure_to = new Date(new Date(leg.expected_departure_from ).setMinutes(1)).toISOString();
            }
            
            leg.vehicles.forEach((v: any) => {
                delete(v.id);
                
            });
            leg.documents_departure?.forEach((d: any) => {

              this.prepareDocumet(d);
            });
            leg.documents_arrival?.forEach((d: any) => {
                this.prepareDocumet(d);
                
            });
            if(!Helpers.isEmptyOrZero(leg.destination_address.id)){
                leg.destination_address = { id: leg.destination_address.id };
            } else{
                delete(leg.destination_address.id);
            }
            if(!Helpers.isEmptyOrZero(leg.origin_address.id)){
                leg.origin_address = { id: leg.origin_address.id };
            }else{
                delete(leg.origin_address.id);
            } 
                
        }
        return j;
    }

    duplicate(transportFull: TransportDto): TransportDto {
        const dto = new TransportDto(this.toJson());
        dto.id = undefined;
        dto.driver = undefined;
        dto.price = undefined;
        dto.reference_number = ''; // Limpiar referencia para evitar duplicados
        dto.comment = '';          // Limpiar comentarios
        // El AF dice "duplicado como plantilla excepto las fechas".
        //dto.legs = this.legs.map(leg => leg.duplicate(transportFull));
        return dto;
    }
}



/**
 * Tramo del transporte (leg)
 */
export class TransportLegDto {
    id?: number;
    order: number;
    transport_id?: number;
    price?: string;
    has_ferry?: boolean;
    kilometers?: string;
    origin_address: Partial<TransportAddress>;
    destination_address: Partial<TransportAddress>;
    connection_type?: ConnectionType;
    expected_departure_from: Date | null;
    expected_departure_to: Date | null;
    expected_arrival_from: Date | null;
    expected_arrival_to: Date | null;
    vehicles: TransportVehicleDto[];
    documents_departure?: TransportDocumentDto[];
    documents_arrival?: TransportDocumentDto[];
    additional_services?: AdditionalServiceDto[];

    constructor(data: any = {}) {
        this.id = data.id;
        this.order = data.order || 1;
        this.transport_id = data.transport_id;
        this.price = data.price;
        this.has_ferry = data.has_ferry ?? false;
        this.kilometers = data.kilometers;

        this.origin_address = data.origin_address;
        this.destination_address = data.destination_address || {};

        this.connection_type = data.connection_type;
        this.expected_departure_from = data.expected_departure_from ? new Date(data.expected_departure_from) : null;
        this.expected_departure_to = data.expected_departure_to ? new Date(data.expected_departure_to) : null;
        this.expected_arrival_from = data.expected_arrival_from ? new Date(data.expected_arrival_from) : null;
        this.expected_arrival_to = data.expected_arrival_to ? new Date(data.expected_arrival_to) : null;
        this.vehicles = data.vehicles?.map((v: any) => new TransportVehicleDto(v)) || 
                       (data.vehicle ? [new TransportVehicleDto(data.vehicle)] : []);
        this.documents_departure = data.documents_departure?.map((d: any) => new TransportDocumentDto(d)) || [];
        this.documents_arrival = data.documents_arrival?.map((d: any) => new TransportDocumentDto(d)) || [];
        this.additional_services = data.additional_services || [];
    }

    static fromJson(json: any): TransportLegDto {
        return new TransportLegDto(json);
    }

    toJson(): any {
        const result: any = {
            id: this.id,
            order: this.order,
            destination_address: this.destination_address,
            expected_departure_from: (this.expected_departure_from && !isNaN(this.expected_departure_from.getTime())) ? this.expected_departure_from.toISOString() : null,
            expected_departure_to: (this.expected_departure_to && !isNaN(this.expected_departure_to.getTime())) ? this.expected_departure_to.toISOString() : null,
            expected_arrival_from: (this.expected_arrival_from && !isNaN(this.expected_arrival_from.getTime())) ? this.expected_arrival_from.toISOString() : null,
            expected_arrival_to: (this.expected_arrival_to && !isNaN(this.expected_arrival_to.getTime())) ? this.expected_arrival_to.toISOString() : null,
            vehicles: this.vehicles.map(v => v.toJson())
        };

        if (this.origin_address) {
            result.origin_address = this.origin_address;
        }

        if (this.connection_type) {
            result.connection_type = this.connection_type;
        }

        if (this.documents_departure && this.documents_departure.length > 0) {
            result.documents_departure = this.documents_departure.map(d => d.toJson());
        }

        if (this.documents_arrival && this.documents_arrival.length > 0) {
            result.documents_arrival = this.documents_arrival.map(d => d.toJson());
        }

        if (this.additional_services && this.additional_services.length > 0) {
            result.additional_services = this.additional_services;
        }

        return result;
    }

    copyWith(updates: Partial<TransportLegDto>): TransportLegDto {
        return new TransportLegDto({
            ...this.toJson(),
            ...updates
        });
    }

    duplicate(transportFull?: TransportDto): TransportLegDto {
        const leg = new TransportLegDto(this.toJson());
        leg.id = undefined;
        // AF [L2199]: "excepto las fechas"
        leg.expected_departure_from = null;
        leg.expected_departure_to = null;
        leg.expected_arrival_from = null;
        leg.expected_arrival_to = null;
        
        leg.vehicles = this.vehicles.map(v => v.duplicate());
        leg.documents_departure = this.documents_departure?.map(d => d.duplicate(transportFull));
        leg.documents_arrival = this.documents_arrival?.map(d => d.duplicate(transportFull));
        
        return leg;
    }
}

/**
 * Vehículo dentro del transporte
 */
export class TransportVehicleDto {
    id?: number;
    license_plate: string;
    fuel_type: FuelType;
    body_type: BodyType;
    brand: string;
    model: string;
    insurance_date_start: string;
    insurance_date_end: string;
    client: number;
    image_url?: string;
    additional_service_ids: number[];
    additional_services?: AdditionalServiceDto[];

    constructor(data: any = {}) {
        this.id = data.id;
        this.license_plate = data.license_plate || '';
        this.fuel_type = data.fuel_type || FuelType.DIESEL;
        this.body_type = data.body_type || BodyType.SEDAN;
        this.brand = data.brand || '';
        this.model = data.model || '';
        this.insurance_date_start = data.insurance_date_start || '';
        this.insurance_date_end = data.insurance_date_end || '';
        this.client = data.client || 0;
        this.image_url = data.image_url;
        this.additional_service_ids = data.additional_service_ids || data.additional_services_ids || [];
        this.additional_services = data.additional_services || [];
    }

    static fromJson(json: any): TransportVehicleDto {
        return new TransportVehicleDto(json);
    }

    toJson(): any {
        return {
            id: this.id,
            license_plate: this.license_plate,
            fuel_type: this.fuel_type,
            body_type: this.body_type,
            brand: this.brand,
            model: this.model,
            insurance_date_start: this.insurance_date_start,
            insurance_date_end: this.insurance_date_end,
            client: this.client,
            image_url: this.image_url,
            additional_service_ids: this.additional_service_ids,
        };
    }
    additionalServicesFrom(additionalServices: AdditionalServiceDto[]) {
        this.additional_services = additionalServices;
    }
    copyWith(updates: Partial<TransportVehicleDto>): TransportVehicleDto {
        return new TransportVehicleDto({
            ...this.toJson(),
            ...updates
        });
    }

    duplicate(): TransportVehicleDto {
        const v = new TransportVehicleDto(this.toJson());
        v.id = undefined;
        return v;
    }
}

/**
 * Documento del transporte
 */
export class TransportDocumentDto {
    id?: number;
    title?: string;
    link?: string;
    document_template_id?: number;
    file_template?: File;

    constructor(data: any = {}) {
        this.id = data.id || data.document_id || null;
        this.title = data.title;
        this.link = data.link ?? "";
        this.document_template_id = data.document_template_id;
        this.file_template = data.file_template;
    }

    static fromJson(json: any): TransportDocumentDto {
        return new TransportDocumentDto(json);
    }

    toJson(): any {
        if(this.id && this.id > 0) {
            return { document_id: this.id };
        }
        const result: any = {
            document_id: this.id && this.id > 0 ? this.id : undefined
        };

        if (this.document_template_id !== undefined) {
            result.document_template_id = this.document_template_id;
        }

/*         if (this.title !== undefined) {
            result.title = this.title;
        } */

        if (this.file_template !== undefined) {
            result.file_template = this.file_template;
        }

        return result;
    }

    copyWith(updates: Partial<TransportDocumentDto>): TransportDocumentDto {
        return new TransportDocumentDto({
            ...this.toJson(),
            ...updates
        });
    }

    duplicate(transportFull?: TransportDto | undefined): TransportDocumentDto {
        const doc = new TransportDocumentDto(this.toJson());
        doc.id = undefined;
        if(transportFull){
            
        }
        // Siempre limpiamos el file_template (evidencia de subida) para que el duplicado sea una plantilla limpia
        
        return doc;
    }
}

/**
 * LegDocument
 */
export class LegDocument {
    comment?: string;

    /**
     * Fecha de Creación
     */
    created_date: Date | null = null;

    document_template_id: number = 0;
    file_sent: string = "";
    file_template: string = "";
    id: number = 0;

    /**
     * Fecha de Modificación
     */
    modified_date: Date | null = null;

    phase: PhaseEnum = PhaseEnum.Departure;
    title: string = "";

    [property: string]: any;
}

/**
 * PhaseEnum, * `DEPARTURE` - Salida
 * * `ARRIVAL` - Llegada
 */
export enum PhaseEnum {
    Arrival = "ARRIVAL",
    Departure = "DEPARTURE",
}